import './Landing.scss'
import { useEffect, useState } from 'react'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { LOGGED_OUT_MENU, SEARCH_STRING_MENU, PLATFORM_EXTERNAL_MENU } from './constants'
import { MessagesLandingApplicationMarketingCard, MessagesLandingPlatformMarketingCard } from '@/dotnet-client'
import { searchFilterGenerator, sortMethod } from './functions'
import { SearchBox, TabMenu, ApplicationCard, MarketingPage } from '@/ux'
import { IconButton } from '@/ui'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { Navigate } from 'react-router-dom'

export function LandingLoggedOut() {
  const [token] = useSessionStorage('token', '')
  const loggedIn = !!token
  const [mainMenuIndex, setMainMenuIndex] = useState<number>(0)
  const [secondMenuIndex, setSecondMenuIndex] = useState<number>(0)
  const [searchString, setSearchString] = useState<string>('')

  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [applicationExternal, setApplicationExternal] = useState<MessagesLandingApplicationMarketingCard[]>([])
  const [platformExternal, setPlatformExternal] = useState<MessagesLandingPlatformMarketingCard[]>([])
  const [marketingPageId, setMarketingPageId] = useState<number>()

  // Remove "Terminology"
  const cleanedPlatformExternalMenu = PLATFORM_EXTERNAL_MENU.filter(
    (item) => item.title.toLowerCase() !== 'terminology'
  )

  const secondMenuOptions = [SEARCH_STRING_MENU, cleanedPlatformExternalMenu]
  const secondMenu = secondMenuOptions[mainMenuIndex]

  useEffect(() => {
    const fetchApplicationExternal = async () => {
      try {
        const result = await dmpClient?.landingApi.getLandingApiGetApplicationExternal()
        setApplicationExternal(result ?? [])
      } catch (err) {
        console.warn(err)
      }
    }

    const fetchPlatformExternal = async () => {
      try {
        const result = await dmpClient?.landingApi.getLandingApiGetPlatformExternal()
        setPlatformExternal(result ?? [])
      } catch (err) {
        console.warn(err)
      }
    }

    if (!isDMPClientLoaded) return

    const menuKey = LOGGED_OUT_MENU[mainMenuIndex].key

    if (menuKey === 'application_external' && applicationExternal.length === 0) {
      fetchApplicationExternal()
    }

    if (menuKey === 'platform_external' && platformExternal.length === 0) {
      fetchPlatformExternal()
    }
  }, [mainMenuIndex, isDMPClientLoaded, dmpClient, applicationExternal.length, platformExternal.length])

  useEffect(() => {
    // Reset second menu + details view when changing top tab
    setSecondMenuIndex(0)
    setMarketingPageId(undefined)
  }, [mainMenuIndex])

  if (loggedIn) {
    return <Navigate to="/workspaces" replace />
  }

  const secondMenuFilterApplicationExternal = (item: MessagesLandingApplicationMarketingCard) => {
    const menuItem = secondMenu[secondMenuIndex]
    return !menuItem || menuItem.title === 'All' || item.tag === menuItem.title
  }

  const secondMenuFilterPlatformExternal = (item: MessagesLandingPlatformMarketingCard) => {
    const menuItem = secondMenu[secondMenuIndex]
    return item.card.groupings?.some((group) => group.value === menuItem?.title)
  }

  const searchFilter = searchFilterGenerator(searchString)

  return (
    <div className="landing">
      {/* Top-Level Tab Menu */}
      <TabMenu
        items={LOGGED_OUT_MENU}
        selectedIndex={mainMenuIndex}
        onChange={(index) => setMainMenuIndex(index)}
        inHeader
      />

      {marketingPageId ? (
        <>
          <div className="second-level-menu-container">
            <IconButton
              icon="arrow-left-outline"
              hover-icon="arrow-left-fill"
              scale={2}
              onClick={() => setMarketingPageId(undefined)}
            />
          </div>
          <MarketingPage id={marketingPageId} />
        </>
      ) : (
        <>
          <div className="second-level-menu-container">
            <div className="empty" />
            <TabMenu items={secondMenu} selectedIndex={secondMenuIndex} onChange={setSecondMenuIndex} />
            <SearchBox searchString={searchString} setSearchString={setSearchString} />
          </div>

          <div className="card-container">
            {LOGGED_OUT_MENU[mainMenuIndex].key === 'application_external' &&
              applicationExternal
                .filter(secondMenuFilterApplicationExternal)
                .sort(sortMethod)
                .filter(searchFilter)
                .map((item, index) => (
                  <ApplicationCard key={index} {...item.card} onClick={() => setMarketingPageId(item.app_id)} />
                ))}

            {LOGGED_OUT_MENU[mainMenuIndex].key === 'platform_external' &&
              platformExternal
                .filter(secondMenuFilterPlatformExternal)
                .sort(sortMethod)
                .filter(searchFilter)
                .map((item, index) => {
                  const { card, email } = item

                  const hasLink = !!card.url || !!email
                  const handleClick = () => {
                    if (card.url) {
                      window.open(card.url, '_blank')
                    } else if (email) {
                      window.location.href = `mailto:${email}`
                    }
                  }

                  return (
                    <ApplicationCard
                      key={index}
                      {...item.card}
                      hover={!!item.card.url || !!item.email}
                      blurb={item.blurb}
                      long_description={item.long_description}
                      email={item.email}
                      onClick={() => {
                        if (item.card.url) {
                          window.open(item.card.url, '_blank')
                        } else if (item.email) {
                          window.open(`mailto:${item.email}`, '_blank')
                        }
                      }}
                    />
                  )
                })}
          </div>
        </>
      )}
    </div>
  )
}
